import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/api/client";
import { Star, ShoppingCart, ShieldCheck, Truck, RotateCcw, Share2, Link2, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import CountdownPill from "@/components/CountdownTimer";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";

function CopyLinkButton({ url }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        // Fallback for older mobile browsers.
        const ta = document.createElement("textarea");
        ta.value = url;
        ta.style.position = "fixed";
        ta.style.top = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      toast.success("Link copied — paste it anywhere.");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Could not copy the link.");
    }
  };
  return (
    <button
      type="button"
      data-testid="pdp-copy-link"
      onClick={copy}
      className={`sm:w-auto inline-flex items-center justify-center gap-2 h-11 px-5 rounded-full border font-medium text-sm transition-colors ${copied ? "border-green-500 text-green-700 bg-green-50" : "border-gray-300 text-gray-700 hover:border-[#F48E2E] hover:text-[#F48E2E]"}`}
      aria-label="Copy product link"
    >
      {copied ? <Check className="h-4 w-4" /> : <Link2 className="h-4 w-4" />}
      {copied ? "Link copied" : "Copy link"}
    </button>
  );
}

export default function ProductDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const { addItem } = useCart();
  const nav = useNavigate();
  const [qty, setQty] = useState(1);
  const [selectedSize, setSelectedSize] = useState("");
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => setP(r.data));
  }, [id]);

  if (!p) return <div className="mx-auto max-w-7xl px-4 lg:px-8 py-20 text-center">Loading…</div>;

  // Normalise sizes: prefer explicit `sizes` array; else split legacy string on / or ,
  const sizes = Array.isArray(p.sizes) && p.sizes.length
    ? p.sizes
    : (p.size ? String(p.size).split(/[,/]/).map((s) => s.trim()).filter(Boolean) : []);
  const requiresSize = sizes.length > 0;

  const soldOut = p.sold_out || Number(p.stock ?? 0) <= 0;
  const addToCart = (goToCheckout = false) => {
    if (soldOut) return;
    if (requiresSize && !selectedSize) {
      toast.error("Please select a size first.");
      return;
    }
    addItem(p, qty, selectedSize);
    if (goToCheckout) nav("/checkout");
    else toast.success("Added to cart");
  };

  const priceStr = `₹${p.price.toLocaleString("en-IN")}`;
  const origStr = p.original_price > p.price ? `₹${p.original_price.toLocaleString("en-IN")}` : null;

  const specRows = [
    { label: "Colour", value: p.color },
    { label: "Material", value: p.material },
  ].filter((r) => r.value && String(r.value).trim().length > 0);

  const hasSpecs = specRows.length > 0 || (p.specifications && String(p.specifications).trim().length > 0);

  return (
    <div className="mx-auto max-w-7xl px-4 lg:px-8 py-10" data-testid="product-detail-page">
      <div className="grid md:grid-cols-2 gap-10">
        <div>
          <div className="relative aspect-[4/5] rounded-3xl overflow-hidden bg-gray-100" data-testid="pdp-main-image">
            <img
              src={(p.images && p.images[activeImg]) || p.image_base64}
              alt={p.name}
              className="w-full h-full object-cover transition-opacity"
            />
            <div className="absolute top-4 right-4">
              <CountdownPill targetIso={p.expires_at} />
            </div>
          </div>
          {(p.images || []).length > 1 && (
            <div className="mt-3 flex gap-2 flex-wrap" data-testid="pdp-thumbs">
              {p.images.map((src, idx) => (
                <button
                  key={idx}
                  data-testid={`pdp-thumb-${idx}`}
                  onClick={() => setActiveImg(idx)}
                  className={`h-16 w-16 rounded-lg overflow-hidden border-2 transition-colors ${activeImg === idx ? "border-[#F48E2E]" : "border-gray-200 hover:border-gray-400"}`}
                  aria-label={`View image ${idx + 1}`}
                >
                  <img src={src} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#F48E2E] font-bold mb-2">{p.category?.replace(/-/g, " ")}</div>
          <h1 className="font-display text-3xl md:text-4xl mb-3">{p.name}</h1>
          <div className="flex items-center gap-1 mb-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <Star key={i} className={`h-4 w-4 ${i <= Math.round(p.rating || 4.5) ? "fill-yellow-400 text-yellow-400" : "text-gray-300"}`} />
            ))}
            <span className="text-sm text-gray-500 ml-2">{p.rating?.toFixed(1) || "4.5"}</span>
          </div>
          <div className="flex items-baseline gap-3 mb-6">
            <div className="font-display text-3xl">{priceStr}</div>
            {origStr && <div className="text-lg text-gray-400 line-through">{origStr}</div>}
            {p.discount_percent > 0 && (
              <span className="px-2 py-1 rounded-full bg-[#111827] text-white text-[11px] font-bold uppercase tracking-wider">-{p.discount_percent}%</span>
            )}
          </div>
          <p className="text-gray-700 mb-6">{p.description}</p>

          {hasSpecs && (
            <div data-testid="pdp-specs" className="mb-6 rounded-2xl border border-gray-200 bg-gray-50 p-5">
              <div className="text-xs uppercase tracking-[0.18em] text-gray-500 font-semibold mb-3">Product Details</div>
              {specRows.length > 0 && (
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                  {specRows.map((r) => (
                    <div key={r.label} className="flex justify-between sm:block border-b border-gray-200 pb-2 sm:border-0 sm:pb-0">
                      <dt className="text-gray-500">{r.label}</dt>
                      <dd className="font-medium text-gray-900 sm:mt-0.5" data-testid={`pdp-spec-${r.label.toLowerCase()}`}>{r.value}</dd>
                    </div>
                  ))}
                </dl>
              )}
              {p.specifications && String(p.specifications).trim().length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="text-xs uppercase tracking-[0.18em] text-gray-500 font-semibold mb-2">Specifications</div>
                  <p className="text-sm text-gray-700 whitespace-pre-line" data-testid="pdp-spec-specifications">{p.specifications}</p>
                </div>
              )}
            </div>
          )}

          {requiresSize && (
            <div className="mb-6" data-testid="pdp-sizes-block">
              <div className="text-xs uppercase tracking-[0.18em] text-gray-500 font-semibold mb-2">Select size <span className="text-red-500 normal-case">*</span></div>
              <div className="flex flex-wrap gap-2">
                {sizes.map((s) => (
                  <button
                    key={s}
                    data-testid={`pdp-size-${s}`}
                    onClick={() => setSelectedSize(s)}
                    className={`text-sm min-w-[52px] px-3 h-9 rounded-full border transition-colors ${selectedSize === s ? "bg-[#F48E2E] text-white border-[#F48E2E]" : "bg-white text-gray-700 border-gray-300 hover:border-[#F48E2E] hover:text-[#F48E2E]"}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
              {selectedSize && <div className="text-xs text-gray-500 mt-2">Selected size: <span className="text-gray-900 font-medium" data-testid="pdp-selected-size">{selectedSize}</span></div>}
            </div>
          )}

          {soldOut ? (
            <div data-testid="pdp-sold-out" className="mb-4 h-11 rounded-full bg-gray-900 text-white text-sm font-bold uppercase tracking-[0.2em] flex items-center justify-center">Sold Out</div>
          ) : (<>
          <div className="flex items-center gap-4 mb-6">
            <div className="flex items-center border border-gray-200 rounded-full">
              <button data-testid="pdp-qty-dec" onClick={() => setQty(Math.max(1, qty - 1))} className="w-9 h-9 rounded-full">-</button>
              <div className="w-8 text-center" data-testid="pdp-qty">{qty}</div>
              <button
                data-testid="pdp-qty-inc"
                onClick={() => {
                  if (qty >= (p.stock || 0)) { toast.error(`Only ${p.stock} in stock`); return; }
                  setQty(qty + 1);
                }}
                disabled={qty >= (p.stock || 0)}
                className="w-9 h-9 rounded-full disabled:opacity-40 disabled:cursor-not-allowed"
              >+</button>
            </div>
            <div className="text-xs text-gray-500">In stock: {p.stock}</div>
          </div>

          <div className="flex gap-3 mb-4">
            <Button data-testid="pdp-add-to-cart" onClick={() => addToCart(false)}
              variant="outline" className="flex-1 rounded-full h-11 border-gray-300">
              <ShoppingCart className="h-4 w-4 mr-2" /> Add to Cart
            </Button>
            <Button data-testid="pdp-buy-now" onClick={() => addToCart(true)}
              className="flex-1 rounded-full h-11 bg-[#F48E2E] hover:bg-[#D97815] text-white">
              Buy Now
            </Button>
          </div>
          </>)}

          {(() => {
            const productUrl = `${window.location.origin}/product/${p.id}?src=whatsapp`;
            const msg = `Check out ${p.name} for ₹${Number(p.price).toLocaleString("en-IN")} on CityShor Pop-Up Bazaar: ${productUrl}`;
            const waUrl = `https://wa.me/?text=${encodeURIComponent(msg)}`;
            return (
              <div className="mb-8 flex flex-col sm:flex-row gap-2">
                <a
                  data-testid="pdp-whatsapp-share"
                  href={waUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 inline-flex items-center justify-center gap-2 h-11 rounded-full border border-[#25D366] text-[#128C7E] hover:bg-[#25D366]/10 font-medium text-sm transition-colors"
                >
                  <Share2 className="h-4 w-4" /> Share on WhatsApp
                </a>
                <CopyLinkButton url={productUrl} />
              </div>
            );
          })()}

          <div className="grid grid-cols-3 gap-3">
            {[
              { i: ShieldCheck, t: "Verified Seller" },
              { i: Truck, t: "India-wide Delivery" },
              { i: RotateCcw, t: "5-day Return" },
            ].map((f, k) => (
              <div key={k} className="p-3 rounded-xl bg-gray-50 border border-gray-200 text-center">
                <f.i className="h-4 w-4 mx-auto mb-1 text-[#F48E2E]" />
                <div className="text-[11px] text-gray-700">{f.t}</div>
              </div>
            ))}
          </div>

          {(() => {
            const zones = [
              { key: "delivery_within_city", label: "Within city", eta: "1 day" },
              { key: "delivery_within_state", label: "Within state", eta: "2-3 days" },
              { key: "delivery_out_of_state", label: "Out of state", eta: "3-5 days" },
            ].filter((z) => p[z.key] !== false);
            const showBlock = zones.length > 0 || typeof p.cod_available === "boolean";
            if (!showBlock) return null;
            return (
              <div className="mt-6 rounded-2xl border border-gray-200 p-4" data-testid="pdp-delivery-block">
                <div className="text-xs uppercase tracking-[0.2em] text-gray-500 font-semibold mb-3">Delivery &amp; Payment</div>
                {zones.length > 0 && (
                  <ul className="space-y-1.5 mb-3">
                    {zones.map((z) => (
                      <li key={z.key} data-testid={`pdp-eta-${z.key}`} className="flex justify-between text-sm">
                        <span className="text-gray-700">{z.label}</span>
                        <span className="text-gray-900 font-medium">{z.eta}</span>
                      </li>
                    ))}
                  </ul>
                )}
                <div className={`inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full ${p.cod_available === false ? "bg-gray-100 text-gray-700" : "bg-green-50 text-green-700"}`} data-testid="pdp-cod-badge">
                  {p.cod_available === false ? "Prepaid only" : "Cash on Delivery available"}
                </div>
              </div>
            );
          })()}

          <p className="text-xs text-gray-500 mt-6">Shipping charges are extra and will be calculated based on the delivery city at checkout.</p>
        </div>
      </div>
    </div>
  );
}
